import React, { useEffect, useState } from "react";
import { Eye, Users } from "@phosphor-icons/react";
import { api } from "../lib/api";

/**
 * Live "visitors today" counter. Pings the backend `/track/visit` endpoint
 * once on mount (idempotent — same IP+day counts once) and polls
 * `/track/stats` every 30 seconds to show the live count + 7-day total.
 *
 * Renders nothing if the API is unreachable so a stats outage never
 * breaks the dashboard.
 *
 * Variants:
 *   - "card" (default): full card with header + 7-day mini bar chart.
 *   - "footer": compact inline pill suitable for footer use.
 */
export default function ViewsWidget({ className = "", variant = "card" }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;

    // 1) Fire-and-forget: register THIS visit so the live count includes us
    api.post("/track/visit").catch(() => {});

    // 2) Poll stats every 30s
    const fetchStats = async () => {
      try {
        const { data } = await api.get("/track/stats");
        if (!cancelled) setStats(data);
      } catch {
        // silent fail — keep showing previous value
      }
    };
    fetchStats();
    const id = setInterval(fetchStats, 30_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  if (!stats) return null;
  const today = stats.today?.uniques || 0;
  const week = stats.last_7_days_total || 0;

  if (variant === "footer") {
    return (
      <span
        className={"inline-flex items-center gap-2 " + className}
        data-testid="views-widget-footer"
        title={`${week.toLocaleString("en-IN")} unique visitors in last 7 days`}
      >
        <Eye size={11} weight="fill" className="text-pos" />
        <span className="font-mono uppercase tracking-widest">
          <span data-testid="views-today-count" className="text-primary-fg">
            {today.toLocaleString("en-IN")}
          </span>
          <span className="ml-1">visitors today</span>
        </span>
        <span
          className="inline-block w-1.5 h-1.5 rounded-full bg-pos animate-pulse"
          title="Live · refreshes every 30s"
        />
      </span>
    );
  }

  const peak = Math.max(1, ...((stats.daily || []).map((d) => d.uniques || 0)));

  return (
    <div
      className={
        "border border-subtle rounded-sm p-4 bg-surface " + className
      }
      data-testid="views-widget"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Eye size={14} weight="fill" className="text-pos" />
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-primary-fg">
            Live Visitors
          </div>
          <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-pos animate-pulse" title="Live · refreshes every 30s" />
        </div>
        <div className="text-[9px] font-mono uppercase tracking-widest text-muted-fg flex items-center gap-1">
          <Users size={9} weight="bold" />
          {week.toLocaleString("en-IN")} this week
        </div>
      </div>
      <div className="flex items-end gap-3">
        <div>
          <div className="font-mono text-3xl tabular-nums text-primary-fg" data-testid="views-today-count">
            {today.toLocaleString("en-IN")}
          </div>
          <div className="text-[9px] font-mono uppercase tracking-widest text-muted-fg">
            visitors today
          </div>
        </div>
        {/* 7-day mini bar chart */}
        <div className="flex-1 flex items-end gap-1 h-9" data-testid="views-7day-chart">
          {(stats.daily || []).map((d) => {
            const h = Math.max(2, Math.round((d.uniques / peak) * 100));
            const isToday = d.day === stats.today?.day;
            return (
              <div
                key={d.day}
                className="flex-1 rounded-sm transition-all"
                style={{
                  height: `${h}%`,
                  background: isToday ? "hsl(var(--signal-positive))" : "hsl(var(--bg-app))",
                  borderTop: isToday ? "none" : "1px solid hsl(var(--border-subtle))",
                  opacity: isToday ? 1 : 0.6,
                }}
                title={`${d.day}: ${d.uniques} visitors`}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
